import React,{useEffect,useMemo,useState} from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const {FiX,FiPlus,FiSave,FiCopy,FiTrash2,FiEye,FiImage,FiVideo,FiCamera,FiCheck,FiCheckCircle,FiAlertCircle,FiChevronDown,FiPlay,FiDownload,FiEyeOff}=FiIcons;
const STORAGE_KEY='bpa_transformation_snapshot_studio_v1';
const CANVAS_WIDTH=2400;
const CANVAS_HEIGHT=1680;
const generateId=()=> `TRANSFORM_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;
const createDefaultProject=()=> ({
  id: generateId(),
  category: 'Ring',
  title: 'Ring Transformation',
  rawImageUrl: '',
  polishedImageUrl: '',
  motionPreviewImageUrl: '',
  watermarkEnabled: true,
  watermarkText: 'BPA SOLUTIONS - WATERMARKED PREVIEW',
  includeContactDetails: true,
  notes: '',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});
const getCategoryTitle=(category)=> {
  switch (category) {
    case 'Ring': return 'Ring Transformation';
    case 'Necklace': return 'Necklace Transformation';
    case 'Earrings': return 'Earring Transformation';
    case 'Custom': return 'Custom Transformation';
    default: return 'Transformation';
  }
};
const loadProjects=()=> {
  try {
    const stored=localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed=JSON.parse(stored);
      return Array.isArray(parsed) ? parsed.map(p=> ({...p,includeContactDetails: p.includeContactDetails !==false,})) : [];
    }
  } catch (e) {
    console.error('[TransformationStudio] Failed to load projects:',e);
  }
  return [];
};
const saveProjects=(projects)=> {
  try {
    localStorage.setItem(STORAGE_KEY,JSON.stringify(projects));
    return true;
  } catch (e) {
    console.error('[TransformationStudio] Failed to save projects:',e);
    return false;
  }
};
const loadCanvasImage=(url)=> {
  return new Promise((resolve,reject)=> {
    if (!url || url.trim()==='') {
      reject(new Error('Empty image URL'));
      return;
    }
    const img=new Image();
    img.crossOrigin='anonymous';
    img.onload=()=> resolve(img);
    img.onerror=()=> reject(new Error(`Failed to load image: ${url}`));
    img.src=url.trim();
  });
};
const drawRoundedRect=(ctx,x,y,width,height,radius)=> {
  ctx.beginPath();
  ctx.moveTo(x + radius,y);
  ctx.lineTo(x + width - radius,y);
  ctx.quadraticCurveTo(x + width,y,x + width,y + radius);
  ctx.lineTo(x + width,y + height - radius);
  ctx.quadraticCurveTo(x + width,y + height,x + width - radius,y + height);
  ctx.lineTo(x + radius,y + height);
  ctx.quadraticCurveTo(x,y + height,x,y + height - radius);
  ctx.lineTo(x,y + radius);
  ctx.quadraticCurveTo(x,y,x + radius,y);
  ctx.closePath();
};
const drawPill=(ctx,x,y,width,height,{fill='#fffaf3',stroke='#eadbc4',text='',textColor='#241811',font='bold 15px sans-serif',dotColor='',}={})=> {
  const radius=height / 2;
  ctx.save();
  drawRoundedRect(ctx,x,y,width,height,radius);
  ctx.fillStyle=fill;
  ctx.fill();
  if (stroke) {
    ctx.strokeStyle=stroke;
    ctx.lineWidth=2;
    ctx.stroke();
  }
  let textX=x + width / 2;
  if (dotColor) {
    ctx.beginPath();
    ctx.arc(x + 22,y + height / 2,6,0,Math.PI * 2);
    ctx.fillStyle=dotColor;
    ctx.fill();
    textX +=8;
  }
  ctx.fillStyle=textColor;
  ctx.font=font;
  ctx.textAlign='center';
  ctx.textBaseline='middle';
  ctx.fillText(text,textX,y + height / 2 + 1);
  ctx.restore();
};
const drawCoverImage=(ctx,image,x,y,width,height)=> {
  const imgRatio=image.width / image.height;
  const frameRatio=width / height;
  let drawWidth,drawHeight,offsetX,offsetY;
  if (imgRatio > frameRatio) {
    drawHeight=height;
    drawWidth=imgRatio * height;
    offsetX=(width - drawWidth) / 2;
    offsetY=0;
  } else {
    drawWidth=width;
    drawHeight=width / imgRatio;
    offsetX=0;
    offsetY=(height - drawHeight) / 2;
  }
  ctx.drawImage(image,x + offsetX,y + offsetY,drawWidth,drawHeight);
};
const drawContainImage=(ctx,image,x,y,width,height)=> {
  const imgRatio=image.width / image.height;
  const frameRatio=width / height;
  let drawWidth,drawHeight,offsetX,offsetY;
  if (imgRatio > frameRatio) {
    drawWidth=width;
    drawHeight=width / imgRatio;
    offsetX=0;
    offsetY=(height - drawHeight) / 2;
  } else {
    drawHeight=height;
    drawWidth=imgRatio * height;
    offsetX=(width - drawWidth) / 2;
    offsetY=0;
  }
  ctx.drawImage(image,x + offsetX,y + offsetY,drawWidth,drawHeight);
};
const drawBlurredContainImage=(ctx,image,x,y,width,height,{radius=28,background='#f5eadb',}={})=> {
  ctx.save();
  drawRoundedRect(ctx,x,y,width,height,radius);
  ctx.clip();
  ctx.fillStyle=background;
  ctx.fillRect(x,y,width,height);
  if (image) {
    ctx.save();
    ctx.filter='blur(24px)';
    ctx.globalAlpha=0.34;
    drawCoverImage(ctx,image,x - 18,y - 18,width + 36,height + 36);
    ctx.restore();
    ctx.save();
    ctx.globalAlpha=1;
    drawContainImage(ctx,image,x,y,width,height);
    ctx.restore();
  }
  ctx.restore();
  ctx.save();
  drawRoundedRect(ctx,x,y,width,height,radius);
  ctx.strokeStyle='#e2c99f';
  ctx.lineWidth=2;
  ctx.stroke();
  ctx.restore();
};
const drawImageInsideRoundedFrame=(ctx,image,x,y,width,height,{fit='cover',radius=28,background='#f5eadb',}={})=> {
  ctx.save();
  drawRoundedRect(ctx,x,y,width,height,radius);
  ctx.fillStyle=background;
  ctx.fill();
  ctx.clip();
  if (image) {
    if (fit==='contain') {
      drawContainImage(ctx,image,x,y,width,height);
    } else {
      drawCoverImage(ctx,image,x,y,width,height);
    }
  }
  ctx.restore();
  ctx.save();
  drawRoundedRect(ctx,x,y,width,height,radius);
  ctx.strokeStyle='#e2c99f';
  ctx.lineWidth=2;
  ctx.stroke();
  ctx.restore();
};
const drawWrappedText=(ctx,text,x,y,maxWidth,lineHeight)=> {
  const words=text.split(' ');
  let line='';
  let currentY=y;
  let lineCount=0;
  for (let i=0;i < words.length;i++) {
    const testLine=line + words[i] + ' ';
    const metrics=ctx.measureText(testLine);
    if (metrics.width > maxWidth && i > 0) {
      ctx.fillText(line,x,currentY);
      line=words[i] + ' ';
      currentY +=lineHeight;
      lineCount++;
    } else {
      line=testLine;
    }
  }
  ctx.fillText(line,x,currentY);
  lineCount++;
  return lineCount * lineHeight;
};
const drawGlassPlayButton=(ctx,centerX,centerY,radius)=> {
  ctx.save();
  ctx.shadowColor='rgba(36,24,17,0.28)';
  ctx.shadowBlur=28;
  ctx.shadowOffsetY=12;
  ctx.beginPath();
  ctx.arc(centerX,centerY,radius,0,Math.PI * 2);
  ctx.fillStyle='rgba(255,250,243,0.34)';
  ctx.fill();
  ctx.shadowColor='transparent';
  ctx.strokeStyle='rgba(255,255,255,0.82)';
  ctx.lineWidth=3;
  ctx.stroke();
  const playSize=radius * 0.64;
  ctx.beginPath();
  ctx.moveTo(centerX - playSize * 0.34,centerY - playSize * 0.55);
  ctx.lineTo(centerX + playSize * 0.62,centerY);
  ctx.lineTo(centerX - playSize * 0.34,centerY + playSize * 0.55);
  ctx.closePath();
  ctx.fillStyle='rgba(255,255,255,0.96)';
  ctx.fill();
  ctx.restore();
};
const drawTiledWatermark=(ctx,{x,y,width,height,text,})=> {
  if (!text || !text.trim()) {
    return;
  }
  ctx.save();
  ctx.beginPath();
  ctx.rect(x,y,width,height);
  ctx.clip();
  ctx.translate(x + width / 2,y + height / 2);
  ctx.rotate(-28 * Math.PI / 180);
  ctx.font='bold 22px sans-serif';
  ctx.textAlign='center';
  ctx.textBaseline='middle';
  const tileWidth=470;
  const tileHeight=210;
  for (let tileX=-width * 1.5;tileX < width * 1.5;tileX +=tileWidth) {
    for (let tileY=-height * 1.5;tileY < height * 1.5;tileY +=tileHeight) {
      ctx.strokeStyle='rgba(36,24,17,0.18)';
      ctx.lineWidth=1;
      ctx.strokeText(text,tileX,tileY);
      ctx.fillStyle='rgba(255,255,255,0.38)';
      ctx.fillText(text,tileX,tileY);
    }
  }
  ctx.restore();
};
const buildSnapshotCanvas=async (project,exportImageOverrides={})=> {
  const canvas=document.createElement('canvas');
  canvas.width=CANVAS_WIDTH;
  canvas.height=CANVAS_HEIGHT;
  const ctx=canvas.getContext('2d');
  const background=ctx.createLinearGradient(0,0,0,CANVAS_HEIGHT);
  background.addColorStop(0,'#fcf6ed');
  background.addColorStop(1,'#f5e7d3');
  ctx.fillStyle=background;
  ctx.fillRect(0,0,CANVAS_WIDTH,CANVAS_HEIGHT);
  const outerMargin=64;
  const headerTop=54;
  const dividerY=112;
  const showcaseTop=164;
  const footerBandHeight=148;
  const footerTop=CANVAS_HEIGHT - footerBandHeight;
  const cardTop=205;
  const cardBottom=footerTop - 24;
  const cardHeight=cardBottom - cardTop;
  const gap=46;
  const cardsStartX=72;
  const cardsTotalWidth=CANVAS_WIDTH - cardsStartX * 2;
  const cardWidth=(cardsTotalWidth - gap * 2) / 3;
  const cardPadding=38;
  const mediaTop=cardTop + 190;
  const mediaWidth=cardWidth - cardPadding * 2;
  const mediaHeight=mediaWidth * 16 / 9;
  ctx.fillStyle='#18110c';
  ctx.font='bold 24px sans-serif';
  ctx.textAlign='left';
  ctx.fillText('BPA SOLUTIONS',outerMargin,headerTop + 24);
  ctx.font='18px sans-serif';
  ctx.fillStyle='#7b6b5e';
  ctx.fillText('Premium Jewellery Visual Studio',outerMargin,headerTop + 52);
  ctx.strokeStyle='#eadbc4';
  ctx.lineWidth=2;
  ctx.beginPath();
  ctx.moveTo(outerMargin,dividerY);
  ctx.lineTo(CANVAS_WIDTH - outerMargin,dividerY);
  ctx.stroke();
  ctx.font='bold 18px sans-serif';
  ctx.fillStyle='#c7a15a';
  ctx.textAlign='left';
  ctx.fillText(project.category.toUpperCase(),outerMargin,showcaseTop + 22);
  const categoryLabelWidth=ctx.measureText(project.category.toUpperCase()).width;
  const lineStart=outerMargin + categoryLabelWidth + 24;
  const lineEnd=CANVAS_WIDTH * 0.5 - 30;
  ctx.strokeStyle='#eadbc4';
  ctx.lineWidth=1.5;
  ctx.beginPath();
  ctx.moveTo(lineStart,showcaseTop + 12);
  ctx.lineTo(lineEnd,showcaseTop + 12);
  ctx.stroke();
  ctx.font='italic 36px "Playfair Display",serif';
  ctx.fillStyle='#7b6b5e';
  ctx.textAlign='left';
  const titleX=CANVAS_WIDTH * 0.5;
  const maxTitleWidth=CANVAS_WIDTH - titleX - outerMargin;
  const truncatedTitle=project.title.length > 50 ? project.title.substring(0,47) + '...' : project.title;
  ctx.fillText(truncatedTitle,titleX,showcaseTop + 22);
  const rawExportSource=exportImageOverrides.raw || project.rawImageUrl.trim();
  const polishedExportSource=exportImageOverrides.polished || project.polishedImageUrl.trim();
  const motionExportSource=exportImageOverrides.motion || project.motionPreviewImageUrl.trim() || exportImageOverrides.polished || project.polishedImageUrl.trim();
  const cardImages={raw: null,polished: null,motion: null};
  const loadErrors=[];
  try {
    if (rawExportSource) {
      cardImages.raw=await loadCanvasImage(rawExportSource);
    }
  } catch (e) {
    loadErrors.push('Raw reference image');
  }
  try {
    if (polishedExportSource) {
      cardImages.polished=await loadCanvasImage(polishedExportSource);
    }
  } catch (e) {
    loadErrors.push('Finished product image');
  }
  try {
    if (motionExportSource) {
      cardImages.motion=await loadCanvasImage(motionExportSource);
    }
  } catch (e) {
    loadErrors.push('Motion preview image');
  }
  const cardDefs=[
    {
      index: 0,
      proofLabel: 'Proof 1',
      secondary: 'Raw Reference',
      heading: 'Original Product Reference',
      image: cardImages.raw,
      fit: 'contain',
      subtext: 'An existing jewellery photo or product page image used as the creative reference.',
    },
    {
      index: 1,
      proofLabel: 'Proof 2',
      secondary: 'Polished Visual',
      heading: 'Finished Product Image',
      image: cardImages.polished,
      fit: 'cover',
      subtext: '',
      premiumBadge: true,
    },
    {
      index: 2,
      proofLabel: 'Proof 3',
      secondary: 'Motion Preview',
      heading: 'Product Video Preview',
      image: cardImages.motion,
      fit: 'cover',
      subtext: '',
      motionBadge: true,
    },
  ];
  cardDefs.forEach((def)=> {
    const cardX=cardsStartX + def.index * (cardWidth + gap);
    const cardY=cardTop;
    ctx.save();
    ctx.shadowColor='rgba(36,24,17,0.08)';
    ctx.shadowBlur=24;
    ctx.shadowOffsetY=8;
    drawRoundedRect(ctx,cardX,cardY,cardWidth,cardHeight,34);
    ctx.fillStyle='#fffaf3';
    ctx.fill();
    ctx.shadowColor='transparent';
    ctx.strokeStyle=def.index===1 ? '#d4b890' : '#eadbc4';
    ctx.lineWidth=def.index===1 ? 3 : 2;
    ctx.stroke();
    ctx.restore();
    ctx.fillStyle='#18110c';
    ctx.font='bold 16px sans-serif';
    ctx.textAlign='left';
    ctx.fillText(def.proofLabel,cardX + cardPadding,cardY + 48);
    const badgeWidth=168;
    const badgeHeight=38;
    const badgeX=cardX + cardWidth - cardPadding - badgeWidth;
    const badgeY=cardY + 28;
    if (def.motionBadge) {
      drawPill(ctx,badgeX,badgeY,176,42,{
        fill: '#160f0b',
        stroke: '#160f0b',
        text: def.secondary,
        textColor: '#fffaf3',
        font: 'bold 14px sans-serif',
        dotColor: '#e44949',
      });
    } else {
      drawPill(ctx,badgeX,badgeY,badgeWidth,badgeHeight,{
        fill: '#fffaf3',
        stroke: '#eadbc4',
        text: def.secondary,
        textColor: '#241811',
        font: 'bold 14px sans-serif',
      });
    }
    ctx.fillStyle='#18110c';
    ctx.font='bold 28px "Playfair Display",serif';
    ctx.textAlign='left';
    ctx.fillText(def.heading,cardX + cardPadding,cardY + 132);
    const mediaX=cardX + cardPadding;
    const mediaY=mediaTop;
    if (def.index===0 && def.image) {
      drawBlurredContainImage(ctx,def.image,mediaX,mediaY,mediaWidth,mediaHeight,{
        radius: 28,
        background: '#f5eadb',
      });
    } else if (def.image) {
      drawImageInsideRoundedFrame(ctx,def.image,mediaX,mediaY,mediaWidth,mediaHeight,{
        fit: def.fit,
        radius: 28,
        background: '#f5eadb',
      });
    } else {
      ctx.save();
      drawRoundedRect(ctx,mediaX,mediaY,mediaWidth,mediaHeight,28);
      ctx.fillStyle='#f5eadb';
      ctx.fill();
      ctx.strokeStyle='#e2c99f';
      ctx.lineWidth=2;
      ctx.stroke();
      ctx.restore();
      ctx.fillStyle='#7b6b5e';
      ctx.font='18px sans-serif';
      ctx.textAlign='center';
      const placeholderY=mediaY + mediaHeight / 2 - 15;
      ctx.fillText('Paste a local export image',mediaX + mediaWidth / 2,placeholderY);
      ctx.font='16px sans-serif';
      ctx.fillText('or compatible hosted URL',mediaX + mediaWidth / 2,placeholderY + 28);
    }
    if (def.premiumBadge) {
      const premiumBadgeWidth=220;
      const premiumBadgeHeight=46;
      const premiumBadgeX=cardX + cardWidth / 2 - premiumBadgeWidth / 2;
      const premiumBadgeY=cardY + 12;
      drawPill(ctx,premiumBadgeX,premiumBadgeY,premiumBadgeWidth,premiumBadgeHeight,{
        fill: '#d5ad63',
        stroke: '#d5ad63',
        text: 'PREMIUM OUTPUT',
        textColor: '#241811',
        font: 'bold 15px sans-serif',
      });
    }
    if (def.motionBadge && def.image) {
      drawGlassPlayButton(ctx,mediaX + mediaWidth / 2,mediaY + mediaHeight / 2,mediaWidth * 0.14);
    }
    if (def.subtext) {
      ctx.fillStyle='#7b6b5e';
      ctx.font='16px sans-serif';
      ctx.textAlign='left';
      const subtextX=cardX + cardPadding;
      const subtextY=mediaY + mediaHeight + 28;
      const maxSubtextWidth=mediaWidth;
      const lineHeight=24;
      drawWrappedText(ctx,def.subtext,subtextX,subtextY,maxSubtextWidth,lineHeight);
    }
  });
  const footerSentenceY=footerTop + 26;
  const promotionalText='At BPA Solutions in Sydney, we transform standard product photos into stunning, studio-quality visuals and model try-on videos—optimised for your online store, social media and marketing campaigns.';
  const promotionalMaxWidth=1820;
  const promotionalX=CANVAS_WIDTH / 2;
  const promotionalLineHeight=30;
  ctx.fillStyle='rgba(78,56,39,0.82)';
  ctx.font='22px sans-serif';
  ctx.textAlign='center';
  drawWrappedText(ctx,promotionalText,promotionalX,footerSentenceY,promotionalMaxWidth,promotionalLineHeight);
  if (project.includeContactDetails !==false) {
    const contactStripY=CANVAS_HEIGHT - 52;
    const contactStripHeight=56;
    const contactStripWidth=720;
    const contactStripX=CANVAS_WIDTH / 2 - contactStripWidth / 2;
    ctx.save();
    ctx.shadowColor='rgba(36,24,17,0.08)';
    ctx.shadowBlur=16;
    ctx.shadowOffsetY=6;
    drawRoundedRect(ctx,contactStripX,contactStripY,contactStripWidth,contactStripHeight,28);
    ctx.fillStyle='#fffaf3';
    ctx.fill();
    ctx.strokeStyle='#eadbc4';
    ctx.lineWidth=2;
    ctx.stroke();
    ctx.shadowColor='transparent';
    ctx.restore();
    ctx.fillStyle='rgba(78,56,39,0.86)';
    ctx.font='22px sans-serif';
    ctx.textAlign='center';
    ctx.fillText('BPA Solutions  |  support@bpasolutions.au  |  +61 425 228 778',CANVAS_WIDTH / 2,contactStripY + 34);
  }
  if (project.watermarkEnabled && project.watermarkText.trim()) {
    drawTiledWatermark(ctx,{
      x: cardsStartX - 10,
      y: cardTop - 10,
      width: cardsTotalWidth + 20,
      height: cardHeight + 20,
      text: project.watermarkText.trim(),
    });
  }
  return {canvas,loadErrors};
};
const downloadSnapshot=async (canvas,format,category)=> {
  const dateStr=new Date().toISOString().slice(0,10);
  const safeCategory=category.toLowerCase().replace(/[^a-z0-9]/g,'-');
  const filename=`bpa-${safeCategory}-transformation-${dateStr}.${format}`;
  return new Promise((resolve,reject)=> {
    const mimeType=format==='png' ? 'image/png' : 'image/jpeg';
    const quality=format==='jpeg' ? 0.96 : undefined;
    canvas.toBlob((blob)=> {
      if (!blob) {
        reject(new Error('Canvas export failed'));
        return;
      }
      const url=URL.createObjectURL(blob);
      const link=document.createElement('a');
      link.href=url;
      link.download=filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      resolve();
    },mimeType,quality);
  });
};
const ImageCard=({src,alt,fallbackText,aspectRatio='aspect-[9/16]',objectFit='object-cover',badge,secondaryBadge,heading,helper,showPlayOverlay=false,})=> {
  const [loadError,setLoadError]=useState(false);
  const hasValidSrc=src && src.trim() !=='';
  const displaySrc=hasValidSrc && !loadError ? src : null;
  return (
    <div className={`relative ${aspectRatio} overflow-hidden rounded-soft border border-white/10 shadow-luxury bg-cream`}>
      {badge && (
        <div className="absolute top-3 left-3 bg-ink/80 backdrop-blur-md px-3 py-1 rounded-full border border-white/10 z-10 flex items-center gap-2">
          <SafeIcon icon={FiCamera} className="w-3 h-3 text-gold" />
          <span className="text-[9px] uppercase tracking-widest text-soft">{badge}</span>
        </div>
      )}
      {secondaryBadge && (
        <div className="absolute top-3 right-3 bg-paper border border-champagne px-3 py-1 rounded-full flex items-center gap-2 z-10">
          <span className="text-[9px] uppercase tracking-widest text-espresso">{secondaryBadge}</span>
        </div>
      )}
      {displaySrc ? (
        <img
          src={displaySrc}
          alt={alt}
          className={`w-full h-full ${objectFit}`}
          onError={()=> setLoadError(true)}
          loading="lazy"
          draggable={false}
          onContextMenu={(e)=> e.preventDefault()}
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-muted p-6">
          <span className="text-xs uppercase tracking-widest mb-2 text-center">{fallbackText}</span>
          <span className="text-[10px] opacity-50 text-center">Paste a hosted image URL</span>
        </div>
      )}
      {showPlayOverlay && displaySrc && (
        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
          <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-md border border-white/40 shadow-lg flex items-center justify-center">
            <SafeIcon icon={FiPlay} className="w-8 h-8 text-white ml-1" />
          </div>
        </div>
      )}
      {heading && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-4 py-3">
          <h3 className="luxury-serif-heading text-white text-sm mb-1">{heading}</h3>
          {helper && <p className="text-[9px] text-soft/80 font-light">{helper}</p>}
        </div>
      )}
    </div>
  );
};
const WatermarkOverlay=({text,enabled})=> {
  if (!enabled || !text || text.trim()==='') {
    return null;
  }
  return (
    <div className="absolute inset-[-25%] z-20 pointer-events-none overflow-hidden rotate-[-28deg]">
      <div className="flex flex-col gap-8 h-full items-center justify-center">
        {Array.from({length: 12}).map((_,i)=> (
          <div key={i} className="text-center opacity-15 whitespace-nowrap">
            <div className="text-[10px] font-bold text-espresso tracking-widest uppercase bg-espresso/10 px-3 py-1 rounded-full">
              {text}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
const ConfirmationModal=({isOpen,onClose,onConfirm,title,message,confirmText='Confirm',confirmColor='red',})=> {
  if (!isOpen) {
    return null;
  }
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-espresso/75 backdrop-blur-sm px-4">
      <div className="w-full max-w-md rounded-[24px] border border-gold/30 bg-ivory shadow-2xl p-8">
        <div className="text-center mb-6">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${confirmColor==='red' ? 'bg-red-50 text-red-600' : 'bg-gold/10 text-gold'}`}>
            <SafeIcon icon={confirmColor==='red' ? FiAlertCircle : FiCheckCircle} className="w-7 h-7" />
          </div>
          <h2 className="text-2xl luxury-serif-heading text-espresso mb-2">{title}</h2>
          <p className="text-sm text-muted leading-relaxed">{message}</p>
        </div>
        <div className="flex flex-col gap-3">
          <button
            onClick={onConfirm}
            className={`w-full px-6 py-3 text-xs uppercase tracking-widest font-bold rounded-full transition-all ${confirmColor==='red' ? 'bg-red-600 text-white hover:bg-red-700' : 'luxury-gold-button'}`}
          >
            {confirmText}
          </button>
          <button
            onClick={onClose}
            className="w-full px-6 py-3 text-xs uppercase tracking-widest font-bold border border-gold/30 text-muted hover:bg-gold/5 rounded-full transition-all"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
const ScreenshotPreviewModal=({isOpen,onClose,project,localExportImages})=> {
  const [screenshotControlsHidden,setScreenshotControlsHidden]=useState(false);
  const rawSrc=localExportImages.raw || project.rawImageUrl;
  const polishedSrc=localExportImages.polished || project.polishedImageUrl;
  const motionSrc=localExportImages.motion || project.motionPreviewImageUrl || localExportImages.polished || project.polishedImageUrl;
  useEffect(()=> {
    if (!isOpen) {
      setScreenshotControlsHidden(false);
      return;
    }
    const handleKeyDown=(e)=> {
      if (e.key==='Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown',handleKeyDown);
    return ()=> document.removeEventListener('keydown',handleKeyDown);
  },[isOpen,onClose]);
  const handleClose=()=> {
    setScreenshotControlsHidden(false);
    onClose();
  };
  if (!isOpen) {
    return null;
  }
  return (
    <div className="fixed inset-0 z-[180] flex flex-col items-center justify-center bg-[#f5eadb] overflow-y-auto">
      {!screenshotControlsHidden && (
        <div className="w-full shrink-0 px-6 py-4 flex items-center justify-center gap-4">
          <div className="bg-paper/90 backdrop-blur-md border border-gold/30 rounded-xl px-6 py-3 shadow-lg flex items-center gap-4">
            <p className="text-[10px] uppercase tracking-widest text-gold font-bold">Screenshot Ready</p>
            <p className="text-xs text-espresso">Capture the clean image area below.</p>
            <button
              onClick={()=> setScreenshotControlsHidden(true)}
              className="luxury-subtle-button px-4 py-1.5 text-[9px]"
            >
              <SafeIcon icon={FiEyeOff} className="w-3 h-3 mr-1.5" />
              Hide Controls
            </button>
            <button
              onClick={handleClose}
              className="p-1.5 text-muted hover:text-espresso transition-colors"
              title="Close Preview (Esc)"
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      <div
        className="w-full flex items-center justify-center py-4"
        style={{
          width: screenshotControlsHidden
            ? 'min(1320px, calc(100vw - 96px), calc((100vh - 40px) * 16 / 9))'
            : 'min(1320px, calc(100vw - 96px), calc((100vh - 120px) * 16 / 9))',
        }}
      >
        <div className="bg-[#fffaf3] rounded-[24px] border border-[#eadbc4] shadow-2xl overflow-hidden w-full h-full flex flex-col">
          <div className="p-8 pb-6 flex-1 flex flex-col" style={{minHeight: 0}}>
            <div className="flex items-center gap-3 mb-6 shrink-0">
              <div className="w-10 h-10 bg-gradient-to-br from-[#c7a15a] to-[#8d6b34] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-[#241811] font-serif text-xl">B</span>
              </div>
              <div className="min-w-0">
                <h2 className="text-2xl font-serif text-[#241811] leading-none">BPA SOLUTIONS</h2>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#7b6b5e] mt-0.5">Premium Jewellery Visual Studio</p>
              </div>
            </div>
            <div className="flex items-center gap-6 mb-6 shrink-0">
              <span className="text-sm tracking-[0.2em] uppercase text-[#c7a15a] font-bold flex-shrink-0">{project.category.toUpperCase()}</span>
              <div className="h-[1px] flex-grow bg-gradient-to-r from-[#eadbc4]/60 to-transparent"></div>
              <span className="font-serif text-[#7b6b5e] text-lg italic truncate">{project.title}</span>
              <div className="h-[1px] flex-grow bg-gradient-to-r from-transparent to-[#eadbc4]/60"></div>
            </div>
            <div className="grid grid-cols-3 gap-6 flex-1 min-h-0" style={{minHeight: 0}}>
              {project.watermarkEnabled && project.watermarkText.trim() && (
                <div className="absolute inset-[-25%] z-10 pointer-events-none overflow-hidden rotate-[-28deg]" style={{pointerEvents: 'none'}}>
                  <div className="flex flex-col gap-12 h-full items-center justify-center">
                    {Array.from({length: 15}).map((_,i)=> (
                      <div key={i} className="text-center opacity-12 whitespace-nowrap">
                        <div className="text-[12px] font-bold text-[#241811] tracking-widest uppercase">
                          {project.watermarkText.trim()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="bg-[#fffaf3] rounded-[24px] border-2 border-[#eadbc4] p-5 shadow-lg flex flex-col min-w-0" style={{minWidth: 0}}>
                <div className="flex justify-between items-center mb-3 shrink-0">
                  <span className="text-[9px] uppercase tracking-widest text-[#241811] font-bold">Proof 1</span>
                  <div className="bg-[#fffaf3] border border-[#eadbc4] px-2.5 py-0.5 rounded-full flex-shrink-0">
                    <span className="text-[7px] uppercase tracking-widest text-[#241811]">Raw Reference</span>
                  </div>
                </div>
                <h3 className="font-serif text-base text-[#241811] mb-3 shrink-0 truncate">Original Product Reference</h3>
                <div className="aspect-[9/16] rounded-[18px] overflow-hidden bg-[#f5eadb] relative border border-[#e2c99f] flex-shrink-0" style={{width: '100%',height: 'auto'}}>
                  {rawSrc ? (
                    <>
                      <img
                        src={rawSrc}
                        alt="Blurred background"
                        className="absolute inset-0 w-full h-full object-cover blur-[20px] scale-110 opacity-30"
                        draggable={false}
                        onContextMenu={(e)=> e.preventDefault()}
                      />
                      <img
                        src={rawSrc}
                        alt="Original Product Reference"
                        className="absolute inset-0 w-full h-full object-contain p-3"
                        draggable={false}
                        onContextMenu={(e)=> e.preventDefault()}
                      />
                    </>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-[#7b6b5e]">
                      <span className="text-[9px] uppercase tracking-widest mb-1">No Image</span>
                    </div>
                  )}
                </div>
                <p className="text-[9px] text-[#7b6b5e] mt-2.5 leading-relaxed flex-1">
                  An existing jewellery photo or product page image used as the creative reference.
                </p>
              </div>
              <div className="bg-[#fffaf3] rounded-[24px] border-2 border-[#d4b890] p-5 shadow-lg flex flex-col min-w-0 ring-2 ring-[#c7a15a]/20" style={{minWidth: 0}}>
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#d5ad63] to-[#c7a15a] text-[#241811] text-[7px] uppercase tracking-widest px-4 py-1 rounded-full font-bold shadow flex-shrink-0" style={{minWidth: 0}}>
                  PREMIUM OUTPUT
                </div>
                <div className="flex justify-between items-center mb-3 mt-2 shrink-0">
                  <span className="text-[9px] uppercase tracking-widest text-[#c7a15a] font-bold">Proof 2</span>
                  <div className="bg-[#fffaf3] border border-[#eadbc4] px-2.5 py-0.5 rounded-full flex-shrink-0">
                    <span className="text-[7px] uppercase tracking-widest text-[#241811]">Polished Visual</span>
                  </div>
                </div>
                <h3 className="font-serif text-base text-[#241811] mb-3 shrink-0 truncate">Finished Product Image</h3>
                <div className="aspect-[9/16] rounded-[18px] overflow-hidden bg-[#f5eadb] relative border border-[#e2c99f] flex-shrink-0" style={{width: '100%',height: 'auto'}}>
                  {polishedSrc ? (
                    <img
                      src={polishedSrc}
                      alt="Finished Product Image"
                      className="absolute inset-0 w-full h-full object-cover"
                      draggable={false}
                      onContextMenu={(e)=> e.preventDefault()}
                    />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-[#7b6b5e]">
                      <span className="text-[9px] uppercase tracking-widest mb-1">No Image</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-[#fffaf3] rounded-[24px] border-2 border-[#eadbc4] p-5 shadow-lg flex flex-col min-w-0" style={{minWidth: 0}}>
                <div className="flex justify-between items-center mb-3 shrink-0">
                  <span className="text-[9px] uppercase tracking-widest text-[#241811] font-bold">Proof 3</span>
                  <div className="bg-[#18110c] px-2.5 py-0.5 rounded-full flex items-center gap-1.5 flex-shrink-0">
                    <span className="w-1 h-1 rounded-full bg-[#e44949] animate-pulse"></span>
                    <span className="text-[7px] uppercase tracking-widest text-[#f8f2e8]">Motion Preview</span>
                  </div>
                </div>
                <h3 className="font-serif text-base text-[#241811] mb-3 shrink-0 truncate">Product Video Preview</h3>
                <div className="aspect-[9/16] rounded-[18px] overflow-hidden bg-[#f5eadb] relative border border-[#e2c99f] flex-shrink-0" style={{width: '100%',height: 'auto'}}>
                  {motionSrc ? (
                    <>
                      <img
                        src={motionSrc}
                        alt="Product Video Preview"
                        className="absolute inset-0 w-full h-full object-cover"
                        draggable={false}
                        onContextMenu={(e)=> e.preventDefault()}
                      />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-14 h-14 rounded-full bg-white/25 backdrop-blur-md border-2 border-white/60 shadow-lg flex items-center justify-center">
                          <SafeIcon icon={FiPlay} className="w-5 h-5 text-white ml-0.5" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-[#7b6b5e]">
                      <span className="text-[9px] uppercase tracking-widest mb-1">No Image</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-5 text-center shrink-0">
              <p className="text-[13px] text-[#4e3827]/85 leading-relaxed max-w-[800px] mx-auto" style={{fontFamily: 'sans-serif'}}>
                At BPA Solutions in Sydney, we transform standard product photos into stunning, studio-quality visuals and model try-on videos—optimised for your online store, social media and marketing campaigns.
              </p>
            </div>
            {project.includeContactDetails !==false && (
              <div className="mt-4 bg-[#fffaf3] rounded-[18px] border border-[#eadbc4] p-4 shadow-md shrink-0">
                <div className="grid grid-cols-2 gap-4 items-center">
                  <div>
                    <p className="text-[8px] uppercase tracking-widest text-[#c7a15a] font-bold mb-1.5">BPA SOLUTIONS CONTACT</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[9px] text-[#7b6b5e]">
                      <span>support@bpasolutions.au</span>
                      <span className="text-[#eadbc4]">•</span>
                      <span>+61 425 228 778</span>
                      <span className="text-[#eadbc4]">•</span>
                      <span>luxury.bpasolutions.au</span>
                      <span className="text-[#eadbc4]">•</span>
                      <span>Sydney, Australia</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[11px] font-serif text-[#241811]">BPA SOLUTIONS</p>
                    <p className="text-[7px] uppercase tracking-[0.2em] text-[#7b6b5e]">Premium Jewellery Visual Studio</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
const TransformationSnapshotStudio=({isOpen,onClose})=> {
  const [projects,setProjects]=useState([]);
  const [currentProject,setCurrentProject]=useState(createDefaultProject());
  const [saveMessage,setSaveMessage]=useState('');
  const [deleteModal,setDeleteModal]=useState({isOpen: false,projectId: null,projectTitle: '',});
  const [mediaLoadErrors,setMediaLoadErrors]=useState({});
  const [exporting,setExporting]=useState(false);
  const [exportError,setExportError]=useState('');
  const [localExportImages,setLocalExportImages]=useState({raw: '',polished: '',motion: '',});
  const [localExportFileNames,setLocalExportFileNames]=useState({raw: '',polished: '',motion: '',});
  const [screenshotPreviewOpen,setScreenshotPreviewOpen]=useState(false);
  const clearLocalExportImages=()=> {
    setLocalExportImages({raw: '',polished: '',motion: '',});
    setLocalExportFileNames({raw: '',polished: '',motion: '',});
  };
  const handleLocalExportFile=(slot,file)=> {
    if (!file) {
      return;
    }
    if (!String(file.type || '').toLowerCase().startsWith('image/')) {
      setExportError('Please choose a valid local image file for export.');
      return;
    }
    const reader=new FileReader();
    reader.onload=()=> {
      setLocalExportImages((current)=> ({...current,[slot]: String(reader.result || ''),}));
      setLocalExportFileNames((current)=> ({...current,[slot]: file.name || 'Local image selected',}));
      setExportError('');
    };
    reader.onerror=()=> {
      setExportError('The selected local image could not be read. Please choose another image file.');
    };
    reader.readAsDataURL(file);
  };
  const clearLocalExportImage=(slot)=> {
    setLocalExportImages((current)=> ({...current,[slot]: '',}));
    setLocalExportFileNames((current)=> ({...current,[slot]: '',}));
  };
  useEffect(()=> {
    if (isOpen) {
      const loaded=loadProjects();
      setProjects(loaded);
      if (loaded.length > 0) {
        setCurrentProject(loaded[0]);
      } else {
        setCurrentProject(createDefaultProject());
      }
    }
  },[isOpen]);
  const handleCategoryChange=(newCategory)=> {
    const autoTitle=getCategoryTitle(newCategory);
    setCurrentProject((prev)=> ({...prev,category: newCategory,title: autoTitle,updatedAt: new Date().toISOString(),}));
  };
  const handleFieldChange=(field,value)=> {
    setCurrentProject((prev)=> ({...prev,[field]: value,updatedAt: new Date().toISOString(),}));
  };
  const handleSaveDraft=()=> {
    if (!currentProject.title || currentProject.title.trim()==='') {
      setSaveMessage('Please enter a transformation title before saving.');
      return;
    }
    const updatedProject={...currentProject,updatedAt: new Date().toISOString(),};
    const existingIndex=projects.findIndex((p)=> p.id===currentProject.id);
    let newProjects;
    if (existingIndex >=0) {
      newProjects=[...projects.slice(0,existingIndex),updatedProject,...projects.slice(existingIndex + 1),];
    } else {
      newProjects=[updatedProject,...projects];
    }
    const saved=saveProjects(newProjects);
    if (saved) {
      setProjects(newProjects);
      setSaveMessage(existingIndex >=0 ? 'Transformation updated successfully.' : 'Transformation saved successfully.');
      setTimeout(()=> setSaveMessage(''),3000);
    } else {
      setSaveMessage('Failed to save. Check browser storage permissions.');
    }
  };
  const handleNewTransformation=()=> {
    const fresh=createDefaultProject();
    setCurrentProject(fresh);
    setSaveMessage('');
    setExportError('');
    clearLocalExportImages();
  };
  const handleOpenProject=(project)=> {
    setCurrentProject({...project,includeContactDetails: project.includeContactDetails !==false});
    setSaveMessage('');
    setExportError('');
    clearLocalExportImages();
  };
  const handleDuplicateProject=(project)=> {
    const duplicated={...project,id: generateId(),title: `${project.title} (Copy)`,includeContactDetails: project.includeContactDetails !==false,createdAt: new Date().toISOString(),updatedAt: new Date().toISOString(),};
    const newProjects=[duplicated,...projects];
    const saved=saveProjects(newProjects);
    if (saved) {
      setProjects(newProjects);
      setCurrentProject(duplicated);
      setSaveMessage('Transformation duplicated.');
      setTimeout(()=> setSaveMessage(''),3000);
    }
    clearLocalExportImages();
  };
  const requestDeleteProject=(project)=> {
    setDeleteModal({isOpen: true,projectId: project.id,projectTitle: project.title,});
  };
  const confirmDeleteProject=()=> {
    const newProjects=projects.filter((p)=> p.id !==deleteModal.projectId);
    const saved=saveProjects(newProjects);
    if (saved) {
      setProjects(newProjects);
      if (currentProject.id===deleteModal.projectId) {
        setCurrentProject(newProjects.length > 0 ? newProjects[0] : createDefaultProject());
      }
      setSaveMessage('Transformation deleted.');
      setTimeout(()=> setSaveMessage(''),3000);
    }
    setDeleteModal({isOpen: false,projectId: null,projectTitle: ''});
  };
  const handleExport=async (format)=> {
    setExporting(true);
    setExportError('');
    try {
      const {canvas,loadErrors}=await buildSnapshotCanvas(currentProject,localExportImages);
      if (loadErrors.length > 0) {
        setExportError('Export could not use one or more hosted image URLs because the remote image host blocks secure Canvas access. Select local export images in the Reliable Export Fallback section, or use image URLs from a CORS-compatible host.');
        setExporting(false);
        return;
      }
      await downloadSnapshot(canvas,format,currentProject.category);
      setSaveMessage(`Transformation exported as ${format.toUpperCase()}.`);
      setTimeout(()=> setSaveMessage(''),3000);
    } catch (e) {
      setExportError('Export could not use one or more hosted image URLs because the remote image host blocks secure Canvas access. Select local export images in the Reliable Export Fallback section, or use image URLs from a CORS-compatible host.');
      console.error('[TransformationStudio] Export error:',e);
    } finally {
      setExporting(false);
    }
  };
  const motionPreviewSource=useMemo(()=> {
    return currentProject.motionPreviewImageUrl.trim() || currentProject.polishedImageUrl.trim();
  },[currentProject.motionPreviewImageUrl,currentProject.polishedImageUrl]);
  if (!isOpen) {
    return null;
  }
  return (
    <>
      <div className="fixed inset-0 z-[150] flex items-center justify-center bg-espresso/65 backdrop-blur-md px-4 py-6">
        <div className="w-full max-w-[1600px] h-full max-h-[90vh] rounded-[32px] border border-gold/30 bg-ivory shadow-2xl flex flex-col overflow-hidden">
          <div className="shrink-0 border-b border-gold/15 bg-paper px-6 py-5 sm:px-8 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gold/10 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiEye} className="w-5 h-5 text-gold" />
              </div>
              <div>
                <h2 className="text-xl luxury-serif-heading text-espresso">Transformation Snapshot Studio</h2>
                <p className="text-[10px] text-muted uppercase tracking-wider font-bold">Create reusable promotional before-and-after layouts</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={handleNewTransformation} className="luxury-subtle-button px-4 py-2 text-[10px]" >
                <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" /> New Transformation
              </button>
              <button onClick={handleSaveDraft} className="luxury-gold-button px-4 py-2 text-[10px]" >
                <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" /> Save Draft
              </button>
              <button
                type="button"
                onClick={()=> setScreenshotPreviewOpen(true)}
                className="luxury-subtle-button px-4 py-2 text-[10px]"
              >
                Preview Screenshot Layout
              </button>
              <button onClick={()=> handleExport('png')} disabled={exporting} className="luxury-subtle-button px-4 py-2 text-[10px] disabled:opacity-50" >
                <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" /> Download PNG
              </button>
              <button onClick={()=> handleExport('jpeg')} disabled={exporting} className="luxury-gold-button px-4 py-2 text-[10px] disabled:opacity-50" >
                <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" /> Download JPEG
              </button>
              <button onClick={onClose} className="p-2 text-muted hover:text-espresso transition-colors" >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden flex">
            <div className="w-80 shrink-0 border-r border-gold/15 bg-cream/50 overflow-y-auto">
              <div className="p-6">
                <h3 className="text-sm font-bold uppercase tracking-widest text-gold mb-4">Saved Transformations</h3>
                {projects.length===0 ? (
                  <p className="text-center text-muted text-xs py-8">No saved transformations yet.</p>
                ) : (
                  <div className="space-y-3">
                    {projects.map((project)=> (
                      <div key={project.id} className={`p-4 border rounded-xl transition-all cursor-pointer ${currentProject.id===project.id ? 'bg-gold/10 border-gold ring-2 ring-gold/30' : 'bg-paper border-gold/20 hover:border-gold/40'}`} >
                        <div className="flex items-start justify-between mb-2">
                          <div className="min-w-0 flex-1">
                            <h4 className="font-serif text-sm text-espresso mb-1 truncate">{project.title}</h4>
                            <p className="text-[9px] text-muted font-mono">{project.category}</p>
                            <p className="text-[8px] text-muted mt-1"> Updated: {new Date(project.updatedAt).toLocaleDateString()} </p>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <button onClick={(e)=> {e.stopPropagation();handleOpenProject(project);}} className="flex-1 luxury-subtle-button px-2 py-1.5 text-[8px]" > Open </button>
                          <button onClick={(e)=> {e.stopPropagation();handleDuplicateProject(project);}} className="flex-1 luxury-subtle-button px-2 py-1.5 text-[8px]" > <SafeIcon icon={FiCopy} className="w-3 h-3 mr-1" /> Duplicate </button>
                          <button onClick={(e)=> {e.stopPropagation();requestDeleteProject(project);}} className="flex-1 border border-red-200 text-red-700 hover:bg-red-50 px-2 py-1.5 text-[8px] font-medium rounded-full transition-all" > <SafeIcon icon={FiTrash2} className="w-3 h-3 mr-1" /> Delete </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-8 sm:px-8">
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-gold mb-4">Transformation Details</h3>
                  <div className="relative">
                    <label className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted absolute -top-3 left-4 bg-ivory px-2">Category</label>
                    <select value={currentProject.category} onChange={(e)=> handleCategoryChange(e.target.value)} className="w-full rounded-xl border border-gold/30 bg-white/70 px-4 py-3 text-espresso outline-none transition focus:border-gold text-sm appearance-none" >
                      <option value="Ring">Ring</option>
                      <option value="Necklace">Necklace</option>
                      <option value="Earrings">Earrings</option>
                      <option value="Custom">Custom</option>
                    </select>
                    <SafeIcon icon={FiChevronDown} className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gold pointer-events-none" />
                  </div>
                  <div className="relative">
                    <input type="text" value={currentProject.title} onChange={(e)=> handleFieldChange('title',e.target.value)} placeholder=" " className="peer relative z-10 w-full bg-transparent border-b border-espresso/20 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent text-espresso caret-espresso text-sm" />
                    <label className="pointer-events-none absolute left-0 -top-3 text-[9px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 peer-focus:-top-3 peer-focus:text-[9px] peer-focus:text-gold font-medium"> Transformation Title </label>
                  </div>
                  <div className="relative">
                    <input type="url" value={currentProject.rawImageUrl} onChange={(e)=> handleFieldChange('rawImageUrl',e.target.value)} placeholder=" " className="peer relative z-10 w-full bg-transparent border-b border-espresso/20 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent text-espresso caret-espresso text-sm" />
                    <label className="pointer-events-none absolute left-0 -top-3 text-[9px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 peer-focus:-top-3 peer-focus:text-[9px] peer-focus:text-gold font-medium"> Original Product Reference Image URL </label>
                  </div>
                  <div className="relative">
                    <input type="url" value={currentProject.polishedImageUrl} onChange={(e)=> handleFieldChange('polishedImageUrl',e.target.value)} placeholder=" " className="peer relative z-10 w-full bg-transparent border-b border-espresso/20 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent text-espresso caret-espresso text-sm" />
                    <label className="pointer-events-none absolute left-0 -top-3 text-[9px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 peer-focus:-top-3 peer-focus:text-[9px] peer-focus:text-gold font-medium"> Finished Product Image URL </label>
                  </div>
                  <div className="relative">
                    <input type="url" value={currentProject.motionPreviewImageUrl} onChange={(e)=> handleFieldChange('motionPreviewImageUrl',e.target.value)} placeholder=" " className="peer relative z-10 w-full bg-transparent border-b border-espresso/20 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent text-espresso caret-espresso text-sm" />
                    <label className="pointer-events-none absolute left-0 -top-3 text-[9px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 peer-focus:-top-3 peer-focus:text-[9px] peer-focus:text-gold font-medium"> Motion Preview Image Override URL </label>
                    <p className="text-[9px] text-muted font-light mt-1">Optional. Leave blank to reuse the Finished Product Image automatically with a glass play-button overlay.</p>
                  </div>
                  <div className="space-y-3 pt-2 border-t border-gold/15">
                    <h4 className="text-[10px] font-bold uppercase tracking-widest text-gold">Reliable Export Fallback</h4>
                    <p className="text-[9px] text-muted leading-relaxed">Some external image hosts allow browser previews but block PNG or JPEG Canvas exports. Choose local image files here only when required. Files remain in this browser session and are never uploaded.</p>
                    <div className="space-y-3">
                      <div className="relative">
                        <input type="file" id="local-raw-export" accept="image/*" onChange={(e)=> handleLocalExportFile('raw',e.target.files?.[0])} className="w-full text-xs text-espresso file:mr-3 file:py-2 file:px-3 file:rounded-full file:border-0 file:text-[9px] file:font-bold file:uppercase file:tracking-widest file:bg-gold/10 file:text-espresso hover:file:bg-gold/20" />
                        <label htmlFor="local-raw-export" className="block text-[9px] font-medium uppercase tracking-[0.18em] text-muted mt-2">Original Reference — Local Export Image</label>
                        {localExportFileNames.raw && (
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-[9px] text-gold truncate">{localExportFileNames.raw}</span>
                            <button type="button" onClick={()=> clearLocalExportImage('raw')} className="text-[9px] uppercase tracking-widest text-muted hover:text-espresso transition-colors">Clear</button>
                          </div>
                        )}
                      </div>
                      <div className="relative">
                        <input type="file" id="local-polished-export" accept="image/*" onChange={(e)=> handleLocalExportFile('polished',e.target.files?.[0])} className="w-full text-xs text-espresso file:mr-3 file:py-2 file:px-3 file:rounded-full file:border-0 file:text-[9px] file:font-bold file:uppercase file:tracking-widest file:bg-gold/10 file:text-espresso hover:file:bg-gold/20" />
                        <label htmlFor="local-polished-export" className="block text-[9px] font-medium uppercase tracking-[0.18em] text-muted mt-2">Finished Product — Local Export Image</label>
                        {localExportFileNames.polished && (
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-[9px] text-gold truncate">{localExportFileNames.polished}</span>
                            <button type="button" onClick={()=> clearLocalExportImage('polished')} className="text-[9px] uppercase tracking-widest text-muted hover:text-espresso transition-colors">Clear</button>
                          </div>
                        )}
                      </div>
                      <div className="relative">
                        <input type="file" id="local-motion-export" accept="image/*" onChange={(e)=> handleLocalExportFile('motion',e.target.files?.[0])} className="w-full text-xs text-espresso file:mr-3 file:py-2 file:px-3 file:rounded-full file:border-0 file:text-[9px] file:font-bold file:uppercase file:tracking-widest file:bg-gold/10 file:text-espresso hover:file:bg-gold/20" />
                        <label htmlFor="local-motion-export" className="block text-[9px] font-medium uppercase tracking-[0.18em] text-muted mt-2">Motion Preview Override — Local Export Image</label>
                        <p className="text-[9px] text-muted font-light mt-1">Optional. Leave blank to reuse the local Finished Product export image.</p>
                        {localExportFileNames.motion && (
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-[9px] text-gold truncate">{localExportFileNames.motion}</span>
                            <button type="button" onClick={()=> clearLocalExportImage('motion')} className="text-[9px] uppercase tracking-widest text-muted hover:text-espresso transition-colors">Clear</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted">Watermark</label>
                    <div className="flex gap-3">
                      <button onClick={()=> handleFieldChange('watermarkEnabled',true)} className={`flex-1 px-4 py-2.5 rounded-xl text-[10px] uppercase tracking-widest font-bold transition-all border ${currentProject.watermarkEnabled ? 'bg-gold text-espresso border-gold shadow-sm' : 'bg-white/50 text-muted border-gold/20 hover:border-gold/40'}`} > Yes </button>
                      <button onClick={()=> handleFieldChange('watermarkEnabled',false)} className={`flex-1 px-4 py-2.5 rounded-xl text-[10px] uppercase tracking-widest font-bold transition-all border ${!currentProject.watermarkEnabled ? 'bg-gold text-espresso border-gold shadow-sm' : 'bg-white/50 text-muted border-gold/20 hover:border-gold/40'}`} > No </button>
                    </div>
                  </div>
                  <div className="relative">
                    <input type="text" value={currentProject.watermarkText} onChange={(e)=> handleFieldChange('watermarkText',e.target.value)} disabled={!currentProject.watermarkEnabled} placeholder=" " className="peer relative z-10 w-full bg-transparent border-b border-espresso/20 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent text-espresso caret-espresso text-sm disabled:opacity-50" />
                    <label className="pointer-events-none absolute left-0 -top-3 text-[9px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 peer-focus:-top-3 peer-focus:text-[9px] peer-focus:text-gold font-medium"> Watermark Text </label>
                    <p className="text-[9px] text-muted font-light mt-1">Used only when Watermark is set to Yes.</p>
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted">Include BPA Solutions Contact Details</label>
                    <div className="flex gap-3">
                      <button onClick={()=> handleFieldChange('includeContactDetails',true)} className={`flex-1 px-4 py-2.5 rounded-xl text-[10px] uppercase tracking-widest font-bold transition-all border ${currentProject.includeContactDetails !==false ? 'bg-gold text-espresso border-gold shadow-sm' : 'bg-white/50 text-muted border-gold/20 hover:border-gold/40'}`} > Yes </button>
                      <button onClick={()=> handleFieldChange('includeContactDetails',false)} className={`flex-1 px-4 py-2.5 rounded-xl text-[10px] uppercase tracking-widest font-bold transition-all border ${currentProject.includeContactDetails ===false ? 'bg-gold text-espresso border-gold shadow-sm' : 'bg-white/50 text-muted border-gold/20 hover:border-gold/40'}`} > No </button>
                    </div>
                  </div>
                  <div className="relative">
                    <textarea value={currentProject.notes} onChange={(e)=> handleFieldChange('notes',e.target.value)} placeholder=" " className="peer relative z-10 w-full bg-transparent border-b border-espresso/20 py-3 focus:outline-none focus:border-gold font-light transition-colors placeholder-transparent resize-none text-espresso caret-espresso text-sm min-h-[100px]" />
                    <label className="pointer-events-none absolute left-0 -top-3 text-[9px] uppercase tracking-widest text-muted transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:top-2 peer-focus:-top-3 peer-focus:text-[9px] peer-focus:text-gold font-medium"> Notes </label>
                  </div>
                  {saveMessage && (
                    <div className={`flex items-center gap-2 text-[10px] p-3 border rounded-soft ${saveMessage.includes('success') || saveMessage.includes('successfully') || saveMessage.includes('duplicated') || saveMessage.includes('deleted') || saveMessage.includes('exported') ? 'text-gold bg-gold/5 border-gold/20' : 'text-red-500 bg-red-50 border-red-200'}`}>
                      <SafeIcon icon={saveMessage.includes('Failed') || saveMessage.includes('could not') ? FiAlertCircle : FiCheckCircle} className="flex-shrink-0" /> {saveMessage}
                    </div>
                  )}
                  {exportError && (
                    <div className="flex items-start gap-2 text-red-500 text-[10px] p-3 border rounded-soft bg-red-50 border-red-200">
                      <SafeIcon icon={FiAlertCircle} className="flex-shrink-0 mt-0.5" /> {exportError}
                    </div>
                  )}
                </div>
                <div className="space-y-6">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-gold mb-4">Live Promotional Preview</h3>
                  <div className="luxury-panel p-6 relative overflow-hidden">
                    <div className="flex items-center gap-6 mb-8">
                      <span className="text-sm tracking-[0.2em] uppercase text-gold font-bold">{currentProject.category}</span>
                      <div className="h-[1px] flex-grow bg-gradient-to-r from-gold/40 to-transparent"></div>
                      <span className="font-serif text-muted text-lg italic">{currentProject.title}</span>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 relative">
                      <ImageCard src={currentProject.rawImageUrl} alt="Original Product Reference" fallbackText="Paste a hosted image URL to preview this section." objectFit="object-contain" badge="Proof 1" secondaryBadge="Raw Reference" heading="Original Product Reference" helper="An existing jewellery photo or product page image used as the creative reference." />
                      <ImageCard src={currentProject.polishedImageUrl} alt="Finished Product Image" fallbackText="Paste a hosted image URL to preview this section." objectFit="object-cover" badge="Proof 2" secondaryBadge="Polished Visual" heading="Finished Product Image" />
                      <div className="relative">
                        <ImageCard src={motionPreviewSource} alt="Product Video Preview" fallbackText="Paste a hosted image URL to preview this section." objectFit="object-cover" badge="Proof 3" secondaryBadge="Motion Preview" heading="Product Video Preview" showPlayOverlay={true} />
                        <div className="absolute top-3 right-3 bg-espressoDeep px-3 py-1 rounded-full flex items-center gap-2 shadow-sm z-20">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                          <span className="text-[9px] uppercase tracking-widest text-soft">Motion Preview</span>
                        </div>
                      </div>
                    </div>
                    <WatermarkOverlay text={currentProject.watermarkText} enabled={currentProject.watermarkEnabled} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ScreenshotPreviewModal
        isOpen={screenshotPreviewOpen}
        onClose={()=> {
          setScreenshotPreviewOpen(false);
        }}
        project={currentProject}
        localExportImages={localExportImages}
      />
      <ConfirmationModal isOpen={deleteModal.isOpen} onClose={()=> setDeleteModal({isOpen: false,projectId: null,projectTitle: ''})} onConfirm={confirmDeleteProject} title="Delete Transformation?" message={`Are you sure you want to delete "${deleteModal.projectTitle}"? This action cannot be undone.`} confirmText="Delete" confirmColor="red" />
    </>
  );
};
export default TransformationSnapshotStudio;